import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ColorsV2 } from 'app/constants/color';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Box, Divider } from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import CustomFilterMenu from './CustomFilterMenu';

// Custom theme: Object defining styling and colors for the DataGrid and related components; customTheme.
const customTheme = createTheme({
  palette: {
    primary: { main: ColorsV2.primary },
    secondary: { main: ColorsV2.secondary },
    background: { default: ColorsV2.primary, paper: ColorsV2.primary },
    text: { primary: ColorsV2.textInfo, secondary: ColorsV2.white },
    divider: ColorsV2.outline,
  },
  components: {
    MuiDataGrid: {
      styleOverrides: {
        // "root": Base container of the DataGrid component.
        root: {
          border: 'none',
          backgroundColor: ColorsV2.primary,
          '& .MuiDataGrid-sortIcon': { color: ColorsV2.acent },
          '& .MuiDataGrid-columnHeaderMenuIcon': { color: ColorsV2.white },
          '& .MuiDataGrid-booleanCell': { fill: ColorsV2.white },
          '& .MuiDataGrid-menuIconButton': { color: ColorsV2.white },
          '& .MuiDataGrid-filterIcon': { fill: ColorsV2.white },
          '& .MuiDataGrid-actionsCell .MuiIconButton-root': { color: ColorsV2.white },
        },
        // "virtualScroller": The scrolling container that holds the rows.
        virtualScroller: { backgroundColor: ColorsV2.primary },
        // "renderingZone": The area where the rows are actually rendered.
        renderingZone: { backgroundColor: ColorsV2.primary },
        // "columnHeaders": Container for the header row displaying column titles.
        columnHeaders: {
          backgroundColor: ColorsV2.secondary,
          color: ColorsV2.white,
          fontWeight: 'bold',
        },
        // "columnHeaderTitle": The text element inside each column header.
        columnHeaderTitle: { color: ColorsV2.white },
        // "columnSeparator": The divider line between individual column headers.
        columnSeparator: { color: ColorsV2.outline },
        // "cell": Styling for each individual cell in the grid.
        cell: {
          // borderBottom: `2px solid ${ColorsV2.outline}`,
          padding: '4px',
          color: ColorsV2.textInfo,
          '&:focus, &:focus-within': {
            border: `2px solid ${ColorsV2.acent}`,
          },
          '&.Mui-selected': { borderBottom: `2px solid ${ColorsV2.outline} !important` },
          '&.MuiDataGrid-cell--editing': { backgroundColor: ColorsV2.secondary + '10' },
          '&.MuiDataGrid-cell--editable': { backgroundColor: ColorsV2.secondary + '05' },
          // "&.filesColumnCell": A custom css class applied to the files column when used in the documentations page. 
          // How this is handled should be changed to allow for better reusability
          // For example a cellClasses prop can be used and spread here
          '&.filesColumnCell': { color: ColorsV2.acent },
        },
        // "row": Styling for each row in the DataGrid.
        row: {
          '&:hover': { backgroundColor: ColorsV2.secondary + '20' },
          '&.Mui-selected': { backgroundColor: ColorsV2.secondary + '40' },
          '&.Mui-selected:hover': { backgroundColor: ColorsV2.secondary + '60' },
          '&.MuiDataGrid-row--editing': { backgroundColor: ColorsV2.secondary + '10' },
          '&.MuiDataGrid-row--selected': { backgroundColor: ColorsV2.secondary + '40' },
        },
        // "footerContainer": The container for the grid footer and pagination controls.
        footerContainer: {
          borderTop: `2px solid ${ColorsV2.outline}`,
          backgroundColor: ColorsV2.primary,
          // "MuiTablePagination-root": Pagination controls
          '& .MuiTablePagination-root': {
            color: ColorsV2.white,
            '& .MuiInputBase-root .MuiSvgIcon-root': { fill: ColorsV2.white },
          },
        },
        // "menu": The dropdown menu for selecting grid actions such as sorting, filtering or show/hide columns.
        menu: {
          '& .MuiDataGrid-menuList': {
            backgroundColor: ColorsV2.secondary,
            color: ColorsV2.white,
            border: '2px solid' + ColorsV2.outline,
          },
        },
        // "panelWrapper": Outer container for all extras menus.
        panelWrapper: { 
          border: '2px solid' + ColorsV2.outline, 
          marginTop: '2px', 
          boxShadow: 'none' 
        },
        // "panelContent": Content area inside extras menus (including filtering and show/hide columns).
        panelContent: {
          backgroundColor: ColorsV2.secondary,
          color: ColorsV2.white,
          '& .MuiSwitch-switchBase': { color: ColorsV2.textInfo },
          '& .MuiSwitch-switchBase.Mui-checked': { color: ColorsV2.acent },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ColorsV2.acent },
          '& .MuiSwitch-track': { backgroundColor: ColorsV2.textInfo },
          '& .MuiSwitch-thumb': { boxShadow: 'none' },
          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { backgroundColor: ColorsV2.textInfo },
        },
        // "panelHeader": Header area used for the show/hide columns menu.
        panelHeader: {
          backgroundColor: ColorsV2.secondary,
          color: ColorsV2.textInfo,
          '& .MuiInputLabel-root.Mui-focused ': { color: ColorsV2.textInfo },
        },
        // "panelFooter": Footer area used for the show/hide columns menu.
        panelFooter: {
          backgroundColor: ColorsV2.secondary,
          '& .MuiButton-text': {
            color: ColorsV2.white,
            transition: 'color 200ms ease-in-out',
            '&:hover': { color: ColorsV2.acent },
          },
        },
        // "filterForm": Styling for the filter form within extras menus.
        filterForm: {
          backgroundColor: ColorsV2.secondary,
          color: ColorsV2.white,
          '& .MuiSvgIcon-root': { color: ColorsV2.white, fill: ColorsV2.white },
          // Default label color
          '& .MuiInputLabel-root': {
            color: ColorsV2.textInfo, // Default label color
          },
          // Focused label color
          '& .MuiInputLabel-root.Mui-focused': {
            color: ColorsV2.white, // Change to desired color
          },
          // TODO: Sort through these and remove irrelevant classes
          '& .MuiInputBase-root': {
            color: ColorsV2.textInfo,
          },
          '& .MuiInputBase-root.Mui-focused': {
            color: ColorsV2.white,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        // "root": Base styling for the Material UI Checkbox component, visible only when allowSelection = true
        root: {
          '& .MuiSvgIcon-root': { fill: ColorsV2.acent, backgroundColor: 'transparent' },
          '&.Mui-checked .MuiSvgIcon-root': { fill: ColorsV2.acent },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: ColorsV2.primary,
          },
        },
      },
    }
  },
});

/**
 * Reusable table component: Displays data in a grid with pagination, sorting, and clipboard-copy features.
 * 
 * height (not required) - Height of the table container, note that this modifies the container NOT the actual grid
 * width (not required) - Width of the table container, note that this modifies the container NOT the actual grid
 * allowSelection- Allow selection (checkbox) for rows
 * rows - Data rows for the grid, this is where the actual datapoints etc. are
 * columns - Column definition for the grid, this should not changed during the components lifetime, this is a template for the structure
 *    - Note the custom properties within columns, apart from standard MUI properties for a column, this component also support:
 *      - copyable (boolean): Allow copy to clipboard on click
 *      - tooltip (boolean): Display tooltip on hover with full field text (Recommended for string fields)
 *      - onClickMenu (boolean): Open a seperate menu on click
 *      - menuCallback (function): The callback function for opening the menu, this component will pass all row data as the first parameter
 */
export default function DataTable({ height = 400, width = '100%', rows = [], columns = [], defaultSort = [], allowSelection = false, editMode = 'cell' }) {
  const [page, setPage] = useState(0);
  // Note that page size (initial page size) has to equal one of the selectable pageSizeOptions
  // otherwise the default options or no options will show up in the menu instead
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setSortModel] = useState([]);
  const [copyAlertOpen, setCopyAlertOpen] = useState(false);
  const [enhancedColumns, setEnhancedColumns] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const { t } = useTranslation('translation', { keyPrefix: 'reusable_components' });


  // Copies the clicked text to clipboard, value = text to copy
  const handleCopy = (value) => {
    if (navigator.clipboard && value) {
      navigator.clipboard.writeText(value);
      setCopyAlertOpen(true);
    }
  };

  // Enhanced columns: Adds custom render logic for boolean values, tooltips, and copy-to-clipboard on click.
  useEffect(() => {
    setEnhancedColumns(
      (columns || []).map((col) => {
        if (col.type === 'actions') {
          return col; // Do not modify the actions column
        }
        return {
          ...col,
          renderCell(params) {
            if (col.type === 'boolean') {
              const icon = params.value ? <CheckIcon /> : <CloseIcon />;
              return col.tooltip ? <Tooltip title={ col.editable ? t('datatable_edit_cell_tooltip') : params.value ? t('datatable_boolean_tooltip_true') : t('datatable_boolean_tooltip_false')}>
                {col.editable && <EditOutlined sx={{width: '14px', height: '14px', marginRight: '4px'}}></EditOutlined>}
                {icon}
                </Tooltip> : icon;
            }
            const value = params.value || '';
            const cell = (
              <span
                onClick={() =>
                  col.onClickMenu && col.menuCallback
                    ? col.menuCallback(params.row)
                    : col.copyable && handleCopy(value)
                }
                style={{
                  cursor: col.copyable ? 'pointer' : col.editable ? 'pointer' : 'default',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
                >
                <span
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {col.renderCell ? col.renderCell(params) : value}
                </span>
                  {col.editable && (
                  <EditOutlined
                    sx={{ width: '14px', height: '14px', marginLeft: '4px' }}
                  />
                  )}
              </span>
            );
            return col.tooltip || col.editable ? <Tooltip title={col.editable ? t('datatable_edit_cell_tooltip') : value}>{cell}</Tooltip> : cell;
          },
        };
      })
    );
  }, [columns]);
  
  useEffect(() => {
    setSortModel(defaultSort);
  }, []);

  // Themeprovider handles styling, theme is used here to make the code more readable since a large amount of classes are targeted
  // The snackbar provides the 'Copied to clipboard' alert
  return (
    <ThemeProvider theme={customTheme}>
      <CustomFilterMenu
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        columns={columns}
      />
      <Divider sx={{ borderColor: ColorsV2.outline}} />
      <Box sx={{ height, width, backgroundColor: ColorsV2.primary }}>
        <DataGrid
          rows={rows || []}
          columns={enhancedColumns}
          pagination
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[8, 20, 100]}
          sortModel={sortModel}
          onSortModelChange={(newModel) => setSortModel(newModel)}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          checkboxSelection={allowSelection}
          disableSelectionOnClick={!allowSelection}
          isCellSelectable={() => false}
          editMode={editMode}
          disableColumnFilter
          sx={{ border: 0 }}
        />
        <Snackbar
          open={copyAlertOpen}
          autoHideDuration={2000}
          onClose={() => setCopyAlertOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setCopyAlertOpen(false)} severity="success" sx={{ width: '100%' }}>
            {t('copy_to_clipboard_alert')}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}
